import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Card,
  Button, Box, Typography, Alert, List, ListItemButton, ListItemText, Chip, Divider,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TimelineIcon from "@mui/icons-material/Timeline";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import AuditTimelineList from "components/AuditTrail/AuditTimelineList";
import { api } from "../../api";

const selectBoxHeight = 45;
const GROUP_PAGE_SIZE = 15;
const PATIENT_PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 80;

const defaultSearchValues = {
  gender: "F",
  search_text: "",
  country_id: "",
  page: 1,
  limit: 10,
};

// Fetches one page from a single audit-log source (contacts or opd/history)
// and normalizes its pagination info onto the in-memory cursor.
async function fetchSourcePage(cursor, fetchPage) {
  if (!cursor.hasMore) return;
  try {
    const response = await fetchPage(cursor.page, GROUP_PAGE_SIZE);
    const groups = response?.data?.audit_groups || [];
    const pagination = response?.data?.pagination;
    cursor.items.push(...groups);
    cursor.hasMore = pagination
      ? cursor.page < pagination.pages
      : groups.length === GROUP_PAGE_SIZE;
    cursor.page += 1;
  } catch {
    // One source failing shouldn't block the other — just stop paging it.
    cursor.hasMore = false;
  }
}

// Each backend source (contact profile/doctor/document changes,
// patient-history changes, IVF lab-performance changes, ...) is
// independently paginated and grouped by save action. This merges an
// arbitrary number of them into one globally-sorted feed by always taking
// the newest of the sources' buffered heads, re-filling a buffer from its
// source whenever it runs dry, until `count` groups are collected.
async function pullNextGroups(cursors, count) {
  const picked = [];
  while (picked.length < count) {
    await Promise.all(cursors.map((cursor) => (
      cursor.items.length === 0 && cursor.hasMore
        ? fetchSourcePage(cursor, cursor.fetchPage)
        : Promise.resolve()
    )));
    let newest = null;
    for (const cursor of cursors) {
      const head = cursor.items[0];
      if (!head) continue;
      if (!newest || new Date(head.created_at) > new Date(newest.items[0].created_at)) {
        newest = cursor;
      }
    }
    if (!newest) break;
    picked.push(newest.items.shift());
  }
  const hasMore = cursors.some((cursor) => cursor.items.length > 0 || cursor.hasMore);
  return { picked, hasMore };
}

function AuditLog() {
  const { patientCode } = useParams();
  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: defaultSearchValues,
  });

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState("");

  // Left pane (patient list) pagination
  const [patientsPage, setPatientsPage] = useState(1);
  const [patientsHasMore, setPatientsHasMore] = useState(false);
  const [loadingMorePatients, setLoadingMorePatients] = useState(false);
  const lastSearchParamsRef = useRef(null);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [auditGroups, setAuditGroups] = useState([]);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [loadingMoreAuditLogs, setLoadingMoreAuditLogs] = useState(false);
  const [auditError, setAuditError] = useState("");

  // Right pane (audit history) merge-pagination cursors — one per backend
  // source. Kept in a ref since they're mutated mid-async-loop and don't
  // themselves need to trigger re-renders.
  const contactCursorRef = useRef({ items: [], page: 1, hasMore: true, fetchPage: null });
  const historyCursorRef = useRef({ items: [], page: 1, hasMore: true, fetchPage: null });
  const ivfCursorRef = useRef({ items: [], page: 1, hasMore: true, fetchPage: null });
  const ivfProcedureCursorRef = useRef({ items: [], page: 1, hasMore: true, fetchPage: null });

  React.useEffect(() => {
    setLoadingCountries(true);
    api.master.getActiveCountries()
      .then((response) => {
        if (response?.data?.countries?.length) {
          setCountries(response.data.countries);
          const defaultCountry = response.data.countries.find(
            (c) => c.is_default === "Y" || c.is_default === true
          );
          if (defaultCountry) setValue("country_id", defaultCountry.id);
        }
      })
      .catch(() => setError("Failed to load countries"))
      .finally(() => setLoadingCountries(false));
  }, [setValue]);

  // Deep-link support: /audit-log/:patientCode — jump straight to a patient's
  // timeline without going through the search form (e.g. from Patient List).
  React.useEffect(() => {
    if (!patientCode) return;
    (async () => {
      setSearching(true);
      setError("");
      try {
        const response = await api.contacts.getPatientDetailsByExternalCode(patientCode);
        const detail = response?.data;
        if (!detail?.contact) {
          setError("Patient not found");
          return;
        }
        const primaryPhone = detail.contact.phones?.[0];
        const primaryDoctor = detail.doctors?.[0];
        const patientObj = {
          contact_id: detail.contact.id,
          external_code: detail.contact.external_code,
          name: detail.contact.full_name,
          phone_number: primaryPhone ? `${primaryPhone.country_code || ''}${primaryPhone.phone_number}` : '',
          doctor_info: primaryDoctor
            ? { doctor_name: primaryDoctor.contact?.full_name || primaryDoctor.name || 'N/A' }
            : null,
        };
        setPatients([patientObj]);
        setPatientsHasMore(false);
        setSearchPerformed(true);
        handleSelectPatient(patientObj);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load patient");
      } finally {
        setSearching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientCode]);

  const applyPatientsResponse = (response, replace) => {
    const contacts = response?.data?.contacts || [];
    const pagination = response?.data?.pagination;
    setPatients((prev) => (replace ? contacts : [...prev, ...contacts]));
    const hasMore = pagination
      ? pagination.page * pagination.limit < pagination.total
      : contacts.length === PATIENT_PAGE_SIZE;
    setPatientsHasMore(hasMore);
    return hasMore;
  };

  const onSearch = async (data) => {
    if (!data.gender || !data.country_id) {
      setError("Gender and Country are required fields");
      return;
    }
    setSearching(true);
    setError("");
    setSelectedPatient(null);
    setAuditGroups([]);
    setAuditHasMore(false);
    try {
      const searchParams = {
        persona_slug: "patient",
        gender: data.gender,
        country_id: data.country_id,
        page: 1,
        limit: PATIENT_PAGE_SIZE,
      };
      if (data.search_text?.trim()) searchParams.search_text = data.search_text.trim();
      lastSearchParamsRef.current = searchParams;

      const response = await api.contacts.searchContacts(searchParams);
      applyPatientsResponse(response, true);
      setPatientsPage(1);
      setSearchPerformed(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to search patients");
      setPatients([]);
      setPatientsHasMore(false);
    } finally {
      setSearching(false);
    }
  };

  // Left pane infinite scroll — fetches the next page of patients from the
  // server instead of revealing an already-fetched array.
  const handleLoadMorePatients = async () => {
    if (loadingMorePatients || !patientsHasMore || !lastSearchParamsRef.current) return;
    setLoadingMorePatients(true);
    try {
      const nextPage = patientsPage + 1;
      const response = await api.contacts.searchContacts({
        ...lastSearchParamsRef.current,
        page: nextPage,
      });
      applyPatientsResponse(response, false);
      setPatientsPage(nextPage);
    } catch {
      // Keep whatever was already loaded; just stop trying to page further.
      setPatientsHasMore(false);
    } finally {
      setLoadingMorePatients(false);
    }
  };

  const handlePatientListScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
      handleLoadMorePatients();
    }
  };

  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setAuditError("");
    setAuditGroups([]);
    setAuditHasMore(false);
    setLoadingAuditLogs(true);

    const contactId = patient.contact_id || patient.contact?.id;
    contactCursorRef.current = {
      items: [], page: 1, hasMore: true,
      fetchPage: (page, limit) => api.contacts.getContactAuditLogs(contactId, page, limit),
    };
    historyCursorRef.current = {
      items: [], page: 1, hasMore: true,
      fetchPage: (page, limit) => api.opd.getContactHistoryAuditLogs(contactId, page, limit),
    };
    ivfCursorRef.current = {
      items: [], page: 1, hasMore: true,
      fetchPage: (page, limit) => api.ivf.getContactLabPerformanceAuditLogs(contactId, page, limit),
    };
    ivfProcedureCursorRef.current = {
      items: [], page: 1, hasMore: true,
      fetchPage: (page, limit) => api.ivf.getContactProcedureAuditLogs(contactId, page, limit),
    };

    try {
      const { picked, hasMore } = await pullNextGroups(
        [contactCursorRef.current, historyCursorRef.current, ivfCursorRef.current, ivfProcedureCursorRef.current], GROUP_PAGE_SIZE,
      );
      setAuditGroups(picked);
      setAuditHasMore(hasMore);
    } catch (err) {
      setAuditError(err.response?.data?.message || "Failed to load audit logs");
      setAuditGroups([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  // Right pane infinite scroll — pulls the next merged page of grouped audit
  // entries (real network requests against both sources, not a client-side
  // reveal of an already-fetched array).
  const handleLoadMoreAuditGroups = async () => {
    if (loadingMoreAuditLogs || !auditHasMore) return;
    setLoadingMoreAuditLogs(true);
    try {
      const { picked, hasMore } = await pullNextGroups(
        [contactCursorRef.current, historyCursorRef.current, ivfCursorRef.current, ivfProcedureCursorRef.current], GROUP_PAGE_SIZE,
      );
      setAuditGroups((prev) => [...prev, ...picked]);
      setAuditHasMore(hasMore);
    } catch {
      setAuditHasMore(false);
    } finally {
      setLoadingMoreAuditLogs(false);
    }
  };

  const handleTimelineScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
      handleLoadMoreAuditGroups();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loadingCountries || (searching && Boolean(patientCode))} />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo">
                <MDTypography variant="h6" color="white">
                  Audit Log
                </MDTypography>
                <MDTypography variant="caption" color="white" opacity={0.8}>
                  Search a patient to view their full change history — doctor re-assignments, profile, phone and address updates, patient history additions/edits/deletions, and IVF Lab Performance saves, edits and deletions
                </MDTypography>
              </MDBox>

              <form onSubmit={handleSubmit(onSearch)} style={{ padding: "20px" }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.gender}>
                          <InputLabel id="al-gender-label">Gender *</InputLabel>
                          <Select {...field} labelId="al-gender-label" label="Gender *" sx={{ height: selectBoxHeight }}>
                            <MenuItem value="M">Male</MenuItem>
                            <MenuItem value="F">Female</MenuItem>
                            <MenuItem value="O">Other</MenuItem>
                            <MenuItem value="U">Unspecified</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="country_id"
                      control={control}
                      rules={{ required: "Country is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.country_id}>
                          <InputLabel id="al-country-label">Country *</InputLabel>
                          <Select {...field} labelId="al-country-label" label="Country *" sx={{ height: selectBoxHeight }}>
                            {countries.map((country) => (
                              <MenuItem key={country.id} value={country.id}>{country.country_name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Controller
                      name="search_text"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Patient Code / Mobile / Couple Code"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter patient code, mobile number, or couple code"
                          sx={{ '& .MuiInputBase-root': { height: selectBoxHeight } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SearchIcon />}
                      disabled={searching}
                      fullWidth
                      sx={{
                        background: "#00798E", color: "#fff", height: selectBoxHeight,
                        fontWeight: 500, '&:hover': { background: "#006375" },
                      }}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {error && (
                <MDBox px={3} pb={2}>
                  <Alert severity="error">{error}</Alert>
                </MDBox>
              )}
            </Card>
          </Grid>

          {searchPerformed && (
            <>
              <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%' }}>
                  <MDBox p={2}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {patients.length} patient{patients.length !== 1 ? 's' : ''} found
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {patients.length === 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                        No patients found. Try adjusting your search.
                      </Typography>
                    )}
                    <List
                      dense
                      sx={{ maxHeight: 520, overflowY: 'auto' }}
                      onScroll={handlePatientListScroll}
                    >
                      {patients.map((patient) => (
                        <ListItemButton
                          key={patient.contact_id}
                          selected={selectedPatient?.contact_id === patient.contact_id}
                          onClick={() => handleSelectPatient(patient)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={patient.name || 'N/A'}
                            secondary={
                              <>
                                {patient.external_code} · {patient.phone_number || 'No phone'}
                                {patient.doctor_info?.doctor_name && (
                                  <Chip label={patient.doctor_info.doctor_name} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                )}
                              </>
                            }
                          />
                        </ListItemButton>
                      ))}
                      {loadingMorePatients && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                          <CircularProgress size={20} sx={{ color: '#00798E' }} />
                        </Box>
                      )}
                      {!loadingMorePatients && patientsHasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleLoadMorePatients}
                            sx={{ color: '#00798E', borderColor: '#00798E', '&:hover': { borderColor: '#006375', background: 'rgba(0,121,142,0.06)' } }}
                          >
                            Load More Patients
                          </Button>
                        </Box>
                      )}
                    </List>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={7}>
                <Card sx={{ height: '100%' }}>
                  <MDBox p={2}>
                    {!selectedPatient ? (
                      <Box sx={{ textAlign: 'center', py: 8, color: '#bbb' }}>
                        <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography sx={{ fontSize: '0.9rem' }}>Select a patient to view their change history</Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                          <Typography variant="subtitle2">
                            {selectedPatient.name} <Typography component="span" variant="caption" color="textSecondary">({selectedPatient.external_code})</Typography>
                          </Typography>
                          {!loadingAuditLogs && auditGroups.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              {auditGroups.length} update{auditGroups.length !== 1 ? 's' : ''}{auditHasMore ? '+' : ''}
                            </Typography>
                          )}
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        {auditError && <Alert severity="error" sx={{ mb: 2 }}>{auditError}</Alert>}
                        <Box sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5 }} onScroll={handleTimelineScroll}>
                          <AuditTimelineList groups={auditGroups} loading={loadingAuditLogs} />
                          {loadingMoreAuditLogs && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                              <CircularProgress size={20} sx={{ color: '#00798E' }} />
                            </Box>
                          )}
                          {!loadingAuditLogs && !loadingMoreAuditLogs && auditHasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleLoadMoreAuditGroups}
                                sx={{ color: '#00798E', borderColor: '#00798E', '&:hover': { borderColor: '#006375', background: 'rgba(0,121,142,0.06)' } }}
                              >
                                Load More
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AuditLog;
